import { Button, FormField, FormRadioCheckboxButton, FormSelect, Header, NotificationHub, RichTable } from "@nef/core"
import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"

import { AppDispatch, RootState } from "../../app/store"
import { SelectOption } from "../../types/common"
import symbolGeneratorColumns, { symbolGeneratorRules, symbolGeneratorRulesColumns } from "../../constants/table/symbolGeneratorColumns"
import { HEADERS, TABLE_EMPTY_STATE_MESSAGE } from "../../constants/common"
import { fetchEnumList, fetchSymbols } from "../../redux/utilities/actions"
import { FileExtensions } from "../../types/_enums"
import { downloadCSVFromString } from "../../utils/csvHandler"

import TableDownloadDropdown from "./TableDownloadDropdown"

interface Filters {
  startingLetters: string | undefined
  instrumentType: SelectOption | undefined
  numberOfSymbols: string | undefined
  includePreviouslyUsedSymbols: boolean
}

const SymbolGenerator = () => {
  const enumsList: any = useSelector((state: RootState) => state.utilities.enumLists)
  const [instrumentTypesData, setInstrumentTypesData] = useState<any[]>([])
  const dispatch = useDispatch<AppDispatch>()
  const [searchData, setSearchData] = useState<any>([])
  const [isDataFetchInProgress, setIsDataFetchInProgress] = useState<boolean>(false)
  const [filters, setFilters] = useState<Filters>({
    startingLetters: undefined,
    instrumentType: undefined,
    numberOfSymbols: undefined,
    includePreviouslyUsedSymbols: false
  })
  const [isFileDownloading, setIsFileDownloading] = useState<boolean>(false)


  // API to get symbols
  const handleGetSymbols = async () => {
    const payload = {
      sStart: filters.startingLetters || 'A',
      count: filters.numberOfSymbols || '1',
      fundCategory: filters.instrumentType?.value,
      used: filters.includePreviouslyUsedSymbols
    }
    try {
      setIsDataFetchInProgress(true)
      const res = await dispatch(fetchSymbols(payload)).unwrap()
      setSearchData(res)
      setIsDataFetchInProgress(false)
    } catch (error: any) {
      setIsDataFetchInProgress(false)
      NotificationHub.send("danger", `${error.code} : ${JSON.stringify(error.message)}`)
    }
  }

  // util to build the dropdown content
  const getDropdownContent = async () => {
    const instrumentType = enumsList
      ?.find((item: any) => item.key === "InstrumentCategory6Enum")
      .value.map((insType: any) => ({ label: insType.description, value: insType.value }))
    if (!instrumentType) return
    setInstrumentTypesData(instrumentType)
  }

  const onSymbolClick = (_: React.MouseEvent<HTMLButtonElement>, value: string) => {
    navigator.clipboard.writeText(value)
    NotificationHub.send("success", "Symbol copied!")
  }

  const handleTableExport = async (type: FileExtensions) => {
    if (searchData?.length) {
      const payload = {
        sStart: filters.startingLetters || 'A',
        count: filters.numberOfSymbols || '1',
        fundCategory: filters.instrumentType?.value,
        used: filters.includePreviouslyUsedSymbols,
        requestHeaders: HEADERS[type]
      }
      try {
        setIsFileDownloading(true)
        const res = await dispatch(fetchSymbols(payload)).unwrap()
        if (!res) {
          NotificationHub.send("primary", `No data available to export!`)
          setIsFileDownloading(false)
          return
        }
        downloadCSVFromString(res, `symbol-generator-list.${type}`, type)
        setIsFileDownloading(false)
      } catch (error: any) {
        setIsFileDownloading(false)
        NotificationHub.send("danger", `${error.status} ${error.message}`)
      }
    }
  }

  useEffect(() => {
    if (!enumsList.length) {
      dispatch(fetchEnumList())
      return
    }
    getDropdownContent()
  }, [enumsList])

  return (
    <>
      <NotificationHub />
      <div className="symbol-gen__container">
        <div className="symbol-gen__section">
          <div className="symbol-gen__filter-wrapper">
            <div className="symbol-gen__filter">
              <FormField
                type="text"
                label="Starting Letter(s)"
                name="startingLetters"
                placeholder="Enter starting letter(s)"
                value={filters.startingLetters}
                onChange={(event: any) => {
                  setFilters((prev: any) => ({ ...prev, startingLetters: event?.value }))
                }}
                optional
              />
            </div>
            <div className="symbol-gen__filter">
              <FormSelect
                label="Instrument Type"
                name="instrumentType"
                placeholder="Select Instrument Type"
                options={instrumentTypesData}
                value={filters.instrumentType}
                isClearable={true}
                onChange={(e: any) => {
                  setFilters((prev: Filters) => ({ ...prev, instrumentType: e?.value }))
                }}
              />
            </div>
            <div className="symbol-gen__filter">
              <FormField
                type="text"
                label="Number Of Symbols"
                name="numberOfSymbols"
                placeholder="Enter number of symbols to generate"
                value={filters.numberOfSymbols}
                onChange={(event: any) => {
                  setFilters((prev: Filters) => ({ ...prev, numberOfSymbols: event?.value }))
                }}
                optional
              />
            </div>
            <div className="symbol-gen__filter">
              <FormRadioCheckboxButton
                id="includeUsedSymbols"
                type="checkbox"
                label="Include Previously Used Symbols"
                checked={filters.includePreviouslyUsedSymbols}
                onChange={(e) => setFilters((prev: Filters) => ({ ...prev, includePreviouslyUsedSymbols: e.checked || false }))}
              />
            </div>
            <Button onClick={handleGetSymbols} isLoading={isDataFetchInProgress} className="symbol-gen__submit-btn">
              Get Symbols
            </Button>
          </div>
          <div className="symbol-gen__filter-wrapper">
            <RichTable
              className="rich-table"
              bodyHeight={380}
              options={{
                columns: symbolGeneratorRulesColumns,
                data: symbolGeneratorRules
              }}
              rowBgPattern="border"
              isColumnSelectorEnabled
              isDynamicRowsEnabled
            >
                <Header className="symbol-gen__header" size={4}>Symbol Assignment Rules</Header>
            </RichTable>
        </div>
        </div>
        <div className="symbol-gen__section">
          <RichTable
            className="rich-table"
            bodyHeight={380}
            options={{
              columns: symbolGeneratorColumns(onSymbolClick),
              data: searchData
            }}
            rowBgPattern="border"
            emptyStateMessage={TABLE_EMPTY_STATE_MESSAGE}
            loading={isDataFetchInProgress}
            isColumnSelectorEnabled
            isDynamicRowsEnabled

          >
             {!isDataFetchInProgress && <Header size={4}>{`Total Count : ${searchData?.length}`}</Header>}
            <TableDownloadDropdown onDownloadClick={handleTableExport} isFileDownloading={isFileDownloading} />
          </RichTable>
        </div>
      </div>
    </>
  )
}

export default SymbolGenerator
